import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Counter, LinkButton, Button, Meter, Parts, Stars } from '../../ui';
import { panelVariants, prefersReducedMotion, scrimVariants } from '../../lib/motion';
import { md } from '../../lib/md';
import { sound } from '../../lib/sound';
import { rankFor } from '../../progress';
import { useLocale, useStrings } from '../../state/useLocale';
import { avatarWords } from '../../lib/strings/avatar-rewards';
import type { ClearOverlayProps } from '../contracts';
import { Postmortem } from './Postmortem';
import { WhatItCost } from './WhatItCost';
import { TwoRuns } from './TwoRuns';
import { COURSE_HREF } from '../../lib/landing';
import { certificatePath } from '../../lib/publicCertificate';
import { OptimizationJourney } from './OptimizationJourney';
import { isOptimizationMission } from '../../lib/optimization';
import { optimizationCopy } from '../../lib/strings/optimization';


/*
 * The clear is one sequence, and these are its beats. The stars land at
 * 260 + i·190 ms (the third at 640) while the XP counts up over the same
 * stretch and settles at 700; the meter ticks a breath after the figure is
 * final; the rank cuts 600 ms after the tick starts, which is the distance
 * motion.css §6 keeps between the two, so it lands 80 ms after the last
 * segment fills. Under reduced motion the kit zeroes both delays and the
 * counter jumps, so the whole standing simply appears.
 */
const XP_COUNT_MS = 700;
const METER_TICK_AT = 900;
const RANK_CUT_AT = METER_TICK_AT + 600;

/**
 * The one moment the product allows itself to be dramatic.
 *
 * Stars land one at a time with a note each, the XP counts up rather than
 * appearing, the rank meter ticks to where that XP put you, and the
 * reflection explains what actually happened before the learner moves on. In
 * an interface with no colour, this is carried by inversion, scale and sound.
 *
 * ## What `next` is, now that a course can branch
 *
 * One mission or none, and the workstation chose it: what a level opens is a
 * *set*, because the last level of a track opens every specialization at once,
 * and this panel has room for one button (App.tsx, `onward`). The rule it was
 * chosen by is forward-not-fork — the continuation of the lane just played,
 * and never a silent pick between lanes — and the reason the panel can live
 * with one button is the branch's own shape: a lane starts a new world, and
 * the buttons below already name a world rather than numbering a level when
 * the level ahead begins one. A learner who wants to see what else opened is
 * one click away on the map, which is the second button here.
 *
 * `next` is also null in more ways than it looks. It means the end of the
 * path, and it equally means the level ahead is in the paid half — the
 * workstation refuses to point a Next button at a level that cannot open, and
 * that refusal arrives here as an absence. The button then reads "Every level
 * cleared", which is true of the first and not of the second; the sentence
 * that would tell those two apart is a string this panel does not have, and
 * writing it belongs with the rest of the paywall copy rather than here.
 */
export function ClearOverlay({ open, mission, result, hintsUsed, freeHints, reflection, next, opener, checks, recall, certificate = null, userId = null, onClose, onPractice, onReplay, onOptimizationComplete, onOptimizationContinue }: ClearOverlayProps) {
  const strings = useStrings();
  const { locale } = useLocale();
  const avatar = avatarWords[locale];
  const s = strings.progress.clear;
  const d = strings.progress.debrief;
  const c = strings.certificateCourse.clear;
  const optimization = optimizationCopy[locale];
  const pilot = Boolean(onReplay && isOptimizationMission(mission.id));
  const [journeyComplete, setJourneyComplete] = useState(false);
  const [journeySkipped, setJourneySkipped] = useState(false);
  useEffect(() => { setJourneyComplete(false); setJourneySkipped(false); }, [mission.id, open, userId]);
  const noteComplete = useCallback((complete: boolean) => { setJourneyComplete(complete); onOptimizationComplete?.(complete); }, [onOptimizationComplete]);
  const noteContinue = useCallback(() => { setJourneySkipped(true); onOptimizationContinue?.(); }, [onOptimizationContinue]);
  const certificateName = strings.certificate.names;
  // Chapter names are a join key in the data and are translated where they
  // are drawn (docs/i18n.md §6.1). Two of them appear on this panel: the
  // world this level belongs to, and the one the next level opens.
  const worldName = (world: string) => strings.home.worlds[world]?.name ?? world;
  /*
   * A checkpoint is not a level, and three things on this panel only mean
   * something for a level. Its number is a position in a track of its own, so
   * "Level 0" named nothing; its catalogue world is the shared "Checkpoints",
   * where the map files it under the chapter it assesses; and it has no hint
   * ladder, so the stars — which price hints and nothing else — had nothing to
   * measure, and drew the full three beside a sentence about not using any.
   * The panel says what did happen: which chapter was assessed, and the badge
   * the pass earned.
   */
  const checkpoint = mission.track === 'checkpoint';
  const assessed = worldName(mission.assesses ?? mission.world);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  const chimed = useRef(false);

  const onLand = useCallback((i: number) => sound.star(i), []);

  useEffect(() => {
    if (!open) {
      chimed.current = false;
      return;
    }
    if (!chimed.current) {
      chimed.current = true;
      sound.clear();
    }
    restoreTo.current = document.activeElement as HTMLElement | null;
    // Focus the panel itself rather than the first button, so a keyboard user
    // hears the dialog before they are standing on "Next level".
    panelRef.current?.focus();
    return () => restoreTo.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      // The panel itself holds focus on open and is deliberately not in the
      // list above. Without this, the first Shift+Tab falls through to the
      // browser, focus escapes behind the scrim, and because it is then
      // neither `first` nor `last` the trap can never take it back.
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === panel || !panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Where the XP put you. Only a clear that earned something is an advance;
  // a replay for the same stars shows the standing settled, because nothing
  // happened to it. A new rank ticks from empty and the name cuts to green
  // (motion.css §6); the same rank ticks from where it was.
  const before = rankFor(result.xpBefore);
  const after = rankFor(result.xpAfter);
  const advanced = result.xpGained > 0;
  const promoted = advanced && after.current.id !== before.current.id;
  const meterFrom = !advanced ? undefined : promoted ? 0 : before.pct;
  // `promoted` compares ids, not names: the name is prose and changes with
  // the language, so a learner who switched languages mid-level would
  // otherwise be promoted twice or not at all.
  const standingNote = promoted
    ? s.rankReached(after.current.name)
    : after.next
      ? s.toNextRank(after.next.xp - result.xpAfter, after.next.name)
      : s.topRank;

  // A rank reached is the one beat of the sequence that is otherwise silent:
  // a single-frame colour cut, easy to miss while reading the reflection. The
  // unlock cue already means "something opened", so it is reused rather than
  // adding a voice to the set.
  useEffect(() => {
    if (!open || !promoted) return;
    const t = window.setTimeout(() => sound.unlock(), prefersReducedMotion() ? 0 : RANK_CUT_AT);
    return () => window.clearTimeout(t);
  }, [open, promoted]);

  // Three stars no longer means nobody was helped: the first rung is free and
  // so is every rung the app offered, so a learner can read one and still
  // clear at full marks. The plain "no hints" line would be a flat lie in
  // that case, and a lie about the scoring is the one this panel cannot
  // afford — the scoring is what the panel is for.
  const note = checkpoint
    ? s.noteCheckpoint(assessed)
    : result.stars === 3
      ? freeHints > 0
        ? s.noteThreeStarsFree
        : s.noteThreeStars
      : result.stars === 2
        ? s.noteTwoStars(hintsUsed + freeHints)
        : s.noteOneStar;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="overlay"
          variants={scrimVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className="clear"
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-title"
            variants={panelVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <span className="label">
              {checkpoint ? s.checkpointWorld(assessed) : s.levelWorld(mission.level, worldName(mission.world))}
            </span>
            <h2 className="clear-title" id="clear-title">
              {checkpoint ? s.checkpointTitle : s.title}
            </h2>
            {/* A checkpoint's title is "Checkpoint: <chapter>", which the line
                above has just said. */}
            {!checkpoint && <p className="clear-sub">{mission.title}</p>}

            {pilot && onReplay && <OptimizationJourney missionId={mission.id} userId={userId} passed open={open}
              onReplay={onReplay} onComplete={noteComplete} onContinue={noteContinue} target="tab" />}

            {!checkpoint && <Stars value={result.stars} className="clear-stars" animateIn onLand={onLand} />}

            <p className="clear-xp">
              {result.xpGained > 0 ? (
                <>
                  +
                  <em>
                    <Counter value={result.xpGained} duration={XP_COUNT_MS} />
                  </em>
                  <span className="unit">{s.xpUnit(result.xpGained)}</span>
                </>
              ) : (
                <>
                  <em>0</em>
                  <span className="unit">{s.xpAlreadyEarned}</span>
                </>
              )}
            </p>

            <div className="clear-standing">
              <p className="clear-standing-rank">
                <span className="label">{s.rank}</span>
                <strong
                  className={promoted ? 'rank is-new' : 'rank'}
                  style={promoted ? { animationDelay: `${RANK_CUT_AT}ms` } : undefined}
                >
                  {after.current.name}
                </strong>
              </p>
              <Meter pct={after.pct} from={meterFrom} delay={METER_TICK_AT} label={s.meterLabel} />
              <span className="note">{standingNote}</span>
            </div>

            <p className="clear-note">{note}</p>

            {/* What the pass did to a certificate (lib/certificates.ts). It
                arrives a beat after the panel, because the answer is read
                once the pass is on record, and is drawn when it lands rather
                than holding the panel back for it.

                An issue is the one piece of news here worth its own block: a
                name and a link to the page somebody else can check. Anything
                less — a pass that moved a certificate closer — is one quiet
                sentence, said here and nowhere else, once. */}
            {/* Always mounted, so a screen reader hears the news when it
                lands; a live region that arrives with its own content is
                announced by some readers and not by others. */}
            <div className="clear-cert-live" aria-live="polite">
              {certificate?.kind === 'issued' && (
                <section className="clear-cert">
                  <h3 className="label">{c.issuedLabel}</h3>
                  {certificate.issued.map((cert) => (
                    <div className="clear-cert-one" key={cert.id}>
                      <p className="clear-cert-name">
                        <strong>{certificateName[cert.certificate]}</strong>
                        {/* The id is machine text and reads left to right in
                            both languages (docs/i18n.md §7). */}
                        <span className="clear-cert-id" dir="ltr">
                          {cert.id}
                        </span>
                      </p>
                      {/* A new tab: the certificate is a page on its own
                          address, and the level behind this panel is still
                          running. */}
                      <LinkButton href={certificatePath(cert.id)} target="_blank" rel="noopener">
                        {c.view}
                      </LinkButton>
                    </div>
                  ))}
                  <p className="note">{c.issuedLine}</p>
                </section>
              )}
              {certificate?.kind === 'toward' && (
                <p className="clear-cert-toward note">
                  {certificate.owns
                    ? c.toward(certificate.passed, certificate.total, certificateName[certificate.certificate])
                    : certificate.passed >= certificate.total
                      ? c.completeFree(certificate.total, certificateName[certificate.certificate])
                      : c.towardFree(certificate.passed, certificate.total, certificateName[certificate.certificate])}
                </p>
              )}
            </div>

            {/* Where this went.
             *
             * The stars say how it ended and the reflection below says what
             * the level was about. This is the part neither of them holds:
             * what the attempt actually cost, and — where the record has one —
             * the earlier level that went the same way.
             *
             * It listed more than this until recently. Every named wrong state
             * the learner had been *told* they were in was read back here in
             * the level's own words, which made it the one thing on the panel
             * that was different every time the same level was cleared. The
             * component that did the telling has been retired (docs/mentor.md)
             * and nothing replaced it, so nobody is told they are in one any
             * more and there is no list of sentences to read back. Recognizing
             * still happens, silently, to price the ladder's first rungs — but
             * a debrief that recited states the learner was never shown would
             * be the panel reading its own notes aloud rather than theirs.
             *
             * What is left is two facts and both are honestly this learner's:
             * the number of checks the attempt took, and the memory. */}
            {/* Agent-first levels: the two runs (TwoRuns.tsx) are the point of
                the clear, so they come before the route: what this run cost,
                what the next one saved, and the way to run it again. */}
            {onReplay && !pilot && <TwoRuns open={open} missionId={mission.id} signedIn={userId !== null} onReplay={onReplay} />}

            {(checks > 0 || recall) && (
              <section className="clear-route" aria-label={d.heading}>
                <h3 className="label">{d.heading}</h3>
                {checks > 0 && <p className="note">{d.checks(checks)}</p>}

                {/* The memory (docs/mentor-plan.md §3.8). The record keeps what
                    this learner has come off worst against, by area, across
                    every level they have cleared, and this is the one place on
                    the clear that says it out loud: you have been here before,
                    and it was the same subject.

                    It says that and stops. It does not say what to do about it,
                    because the answer to *this* level arriving by the back door
                    is the one thing no part of the help system has ever done.

                    It borrowed the mentor's own `recall` line to say it, which
                    was already marked here as the wrong home for it. That line
                    went with the rest of the cue catalogue, so the sentence is
                    now `debrief.earlier` — this panel's, named after the panel
                    it is drawn on, in both languages. */}
                {recall && <p className="note">{d.earlier(recall.level, recall.title)}</p>}
                {/* More than one check means at least one failed: the moment a
                    blameless note is worth writing (Postmortem.tsx). */}
                {checks > 1 && <Postmortem key={mission.id} mission={mission.id} userId={userId} />}
              </section>
            )}

            {/* What the learner's agent spent, after a metered pass
                (docs/efficiency-plan.md). It draws nothing for a pass by
                hand, for a guest, or while feedback is not shown to this
                learner, and lands a beat after the panel when it does. */}
            {!onReplay && <WhatItCost open={open} missionId={mission.id} signedIn={userId !== null} />}

            <div className="clear-reflection prose scroll" dangerouslySetInnerHTML={md(reflection)} />

            {!!result.avatarRewards && <div className="clear-avatar-reward">
              <p className="note">{avatar.newRewards(result.avatarRewards)}</p>
              <LinkButton variant="quiet" href="?profile=me">{avatar.customize}</LinkButton>
            </div>}

            {/* The long version. Offered here and never shown here: the
                overlay is four seconds of stars and a rank moving, and a
                thousand words inside it would be scrolled past by everyone.
                The article has its own screen and its own URL, and it stays
                reachable from the map long after this dismisses. */}
            <LinkButton variant="quiet" wide href={`?debrief=${encodeURIComponent(mission.id)}`}>
              {s.readDebrief}
            </LinkButton>

            {/* A chapter is ending. This is the one moment a learner is
                genuinely available to be told why the next one exists —
                their hand is already on the button that says what happens
                next — so when there is an opener for the world ahead, that
                button goes to the essay and the essay goes on to the level.
                It is offered once per browser and skippable in one click;
                `opener` is null on every other clear. */}
            {next && opener && (
              <p className="clear-chapter">
                {/* The chapter's name is set in bold in the middle of this
                    sentence, and where the middle is is part of the
                    translation. So the catalogue returns the sentence as
                    parts with the `<strong>` already placed in it. */}
                <Parts
                  of={s.chapterHandover(next.level, <strong>{worldName(opener.world)}</strong>, opener.minutes)}
                />
              </p>
            )}

            <div className="clear-actions">
              {onPractice && <Button variant="quiet" onClick={onPractice}>{strings.learning.transfer}</Button>}
              {pilot && !journeyComplete && !journeySkipped ? (
                <Button variant="quiet" onClick={noteContinue}>{optimization.continue}</Button>
              ) : next && opener ? (
                <>
                  <LinkButton variant="solid" size="lg" href={`?opener=${encodeURIComponent(opener.slug)}`}>
                    {s.nextWorld(worldName(opener.world))}
                  </LinkButton>
                  <LinkButton variant="quiet" href={`?mission=${encodeURIComponent(next.id)}`}>
                    {s.straightToLevel(next.level)}
                  </LinkButton>
                </>
              ) : next ? (
                <LinkButton variant="solid" size="lg" href={`?mission=${encodeURIComponent(next.id)}`}>
                  {next.track === 'checkpoint' ? next.title : s.nextLevel(next.level)}
                </LinkButton>
              ) : (
                <LinkButton variant="solid" size="lg" href={COURSE_HREF}>
                  {s.everyLevelCleared}
                </LinkButton>
              )}
              <LinkButton variant="quiet" href={COURSE_HREF}>
                {s.levelList}
              </LinkButton>
              <Button variant="quiet" onClick={onClose}>
                {s.stayHere}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ClearOverlay;
